import type { HoverSpeed } from './hoverTypes'

export const OVERFLOW_BUBBLE_CLASS = 'hoverBubble--overflow'

export interface NativeTitleHoverProfile {
  speed: HoverSpeed
  bubbleClassName?: string
}

const ASSISTIVE_TEXT_CLASS_NAMES = new Set([
  'sronly',
  'screenreaderonly',
  'visuallyhidden',
])

const normaliseText = (value: string) => value.replace(/\s+/g, ' ').trim()

const hasAssistiveTextClass = (el: Element) => {
  for (const className of Array.from(el.classList)) {
    const normalized = className.toLowerCase().replace(/[_-]/g, '')
    if (ASSISTIVE_TEXT_CLASS_NAMES.has(normalized)) return true
  }
  return false
}

const isHiddenFromVisibleText = (el: Element, boundary: HTMLElement) => {
  let current: Element | null = el
  while (current && current !== boundary.parentElement) {
    if (current.getAttribute('aria-hidden') === 'true') return true
    if (hasAssistiveTextClass(current)) return true
    const view: Window | null = current.ownerDocument.defaultView
    const style = view?.getComputedStyle(current)
    if (style && (style.display === 'none' || style.visibility === 'hidden')) return true
    if (current instanceof HTMLElement) {
      if (current.hidden) return true
    }
    if (current === boundary) break
    current = current.parentElement
  }
  return false
}

export const getNativeTitleVisibleText = (anchor: HTMLElement) => {
  const doc = anchor.ownerDocument
  if (!doc.createTreeWalker) {
    return normaliseText(typeof anchor.innerText === 'string' ? anchor.innerText : anchor.textContent ?? '')
  }
  const walker = doc.createTreeWalker(anchor, 4)
  const chunks: string[] = []
  let node = walker.nextNode()
  while (node) {
    const parent = node.parentElement
    if (parent && !isHiddenFromVisibleText(parent, anchor)) {
      const text = normaliseText(node.textContent ?? '')
      if (text) chunks.push(text)
    }
    node = walker.nextNode()
  }
  return normaliseText(chunks.join(' '))
}

export const classifyNativeTitleHover = (anchor: HTMLElement, title: string): NativeTitleHoverProfile => {
  const titleText = normaliseText(title)
  const visibleText = getNativeTitleVisibleText(anchor)
  const iconOnly = visibleText.length === 0 || visibleText === titleText
  if (iconOnly) return { speed: 'immediate' }
  return { speed: 'delayed', bubbleClassName: OVERFLOW_BUBBLE_CLASS }
}
